import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock, User, Globe, Building2, ChevronDown, AlertCircle } from 'lucide-react';

import { useAuth } from '../../providers/AuthProvider';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { setLanguage, type SupportedLanguage, languageConfig } from '../../i18n/config';
import { api, type ApiError } from '../../lib/api';
import type { Company } from '../../types/auth';

export default function LoginPage() {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const { login, isLoading } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<number | undefined>(undefined);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Companies state
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);

  const currentLanguage = i18n.language as SupportedLanguage;
  const otherLanguage: SupportedLanguage = currentLanguage === 'ar' ? 'en' : 'ar';
  const isRTL = currentLanguage === 'ar';

  // Fetch companies on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setCompaniesLoading(true);
        setCompaniesError(null);
        const response = await api.get<Company[]>('/auth/companies');
        const companiesData = response.data || [];
        setCompanies(companiesData);
        // Auto-select if only one company
        if (companiesData.length === 1 && companiesData[0]) {
          setSelectedCompany(companiesData[0].cocode);
        }
      } catch (err) {
        const apiError = err as ApiError;
        setCompaniesError(apiError.message || t('common:errors.generic'));
        // Fallback: allow login without company selection
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompanies();
  }, [t]);

  const handleLanguageSwitch = () => {
    setLanguage(otherLanguage);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username.trim() || !password.trim()) {
      setError(t('common:validation.required'));
      return;
    }

    // Require company selection if companies are available
    if (companies.length > 1 && selectedCompany === undefined) {
      setError(isRTL ? 'يرجى اختيار الشركة' : 'Please select a company');
      return;
    }

    try {
      await login({
        username: username.trim(),
        password,
        cocode: selectedCompany,
      });
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.code === 'INVALID_CREDENTIALS') {
        setError(t('errors.invalidCredentials'));
      } else if (apiError.code === 'ACCOUNT_LOCKED') {
        setError(t('errors.accountLocked'));
      } else if (apiError.code === 'ACCOUNT_DISABLED') {
        setError(t('errors.accountDisabled'));
      } else {
        setError(apiError.message || t('common:errors.generic'));
      }
    }
  };

  const getCompanyDisplayName = (company: Company) => {
    return isRTL ? company.nameAr : company.name;
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gradient-to-br from-primary-50 to-secondary-100">
      {/* Language switcher */}
      <div className="absolute top-4 ltr:right-4 rtl:left-4">
        <button
          type="button"
          onClick={handleLanguageSwitch}
          className="flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-secondary-700 shadow-sm hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <Globe className="h-4 w-4" />
          {languageConfig[otherLanguage].nativeName}
        </button>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md px-4">
        {/* Logo and title */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 rounded-full bg-primary-600 flex items-center justify-center">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-secondary-900">
            {t('common:appName')}
          </h1>
          <p className="mt-2 text-secondary-600">
            {t('common:appDescription')}
          </p>
        </div>

        {/* Login form card */}
        <div className="bg-white shadow-xl rounded-lg px-6 py-8 sm:px-10">
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">
            {t('login.title')}
          </h2>
          <p className="text-sm text-secondary-600 mb-6">
            {t('login.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error message */}
            {error && (
              <div
                className="rounded-md bg-red-50 p-4 text-sm text-red-700 border border-red-200 flex items-start gap-2"
                role="alert"
              >
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Company selector */}
            {companies.length > 0 && (
              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-secondary-700 mb-1"
                >
                  {isRTL ? 'الشركة' : 'Company'}
                  {companies.length > 1 && <span className="text-red-500 ltr:ml-1 rtl:mr-1">*</span>}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 ltr:left-0 rtl:right-0 flex items-center ltr:pl-3 rtl:pr-3 pointer-events-none">
                    <Building2 className="h-5 w-5 text-secondary-400" />
                  </div>
                  <select
                    id="company"
                    name="company"
                    value={selectedCompany ?? ''}
                    onChange={(e) => setSelectedCompany(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    disabled={isLoading || companiesLoading || companies.length === 1}
                    className="block w-full appearance-none rounded-md border border-secondary-300 bg-white py-2.5 ltr:pl-10 rtl:pr-10 ltr:pr-10 rtl:pl-10 text-secondary-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-secondary-50 disabled:cursor-not-allowed sm:text-sm"
                  >
                    {companies.length > 1 && (
                      <option value="">
                        {isRTL ? '-- اختر الشركة --' : '-- Select Company --'}
                      </option>
                    )}
                    {companies.map((company) => (
                      <option key={company.cocode} value={company.cocode}>
                        {getCompanyDisplayName(company)}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 ltr:right-0 rtl:left-0 flex items-center ltr:pr-3 rtl:pl-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-secondary-400" />
                  </div>
                </div>
                {companiesError && (
                  <p className="mt-1 text-xs text-amber-600">
                    {isRTL ? 'تعذر تحميل الشركات' : 'Could not load companies'}
                  </p>
                )}
              </div>
            )}

            {/* Username field */}
            <Input
              type="text"
              name="username"
              label={t('login.username')}
              placeholder={t('login.usernamePlaceholder')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              leftIcon={<User className="h-5 w-5" />}
              isRequired
              disabled={isLoading}
              autoComplete="username"
              autoFocus={companies.length <= 1}
            />

            {/* Password field */}
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                label={t('login.password')}
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<Lock className="h-5 w-5" />}
                isRequired
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-8 ltr:right-3 rtl:left-3 text-secondary-400 hover:text-secondary-600"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Remember me and forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ltr:ml-2 rtl:mr-2 text-sm text-secondary-600">
                  {t('login.rememberMe')}
                </span>
              </label>
              <button
                type="button"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                {t('login.forgotPassword')}
              </button>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={isLoading}
            >
              {isLoading ? t('login.loggingIn') : t('login.loginButton')}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-secondary-500">
          Nano Systems &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
